import PresentationReducerBuilder from "./build-presentation-reducer"
import {
    CLUSTER_ITEM_REQUEST as ITEM_REQUEST,
    CLUSTER_ITEM_RESPONSE as ITEM_RESPONSE,
    CLUSTER_ITEM_SAVE_RESPONSE,
    CLUSTER_ITEM_CACHED_RESPONSE as ITEM_CACHED_RESPONSE,
    CLUSTER_LIST_SEARCH_REQUEST as LIST_SEARCH_REQUEST,
    CLUSTER_LIST_REQUEST as LIST_PAGE_REQUEST,
    CLUSTER_LIST_RESPONSE as LIST_RESPONSE,
    CLUSTER_LIST_CACHED_RESPONSE as LIST_CACHED_RESPONSE,
    CLUSTER_SET_FILTER as SET_FILTER,
    CLUSTER_SET_EDIT_MODE as SET_EDIT_MODE,
    CLUSTER_UPDATE_FILTER_CRITERIA as UPDATE_FILTER_CRITERIA,
    CLUSTER_CLEAR_FILTER_CRITERIA as CLEAR_FILTER_CRITERIA,
    CLUSTER_UPDATE_IMAGE as UPDATE_IMAGE,
    CLUSTER_UPDATE_MODEL as UPDATE_MODEL,
    CLUSTER_APPLY_MODEL_UPDATE as APPLY_MODEL_UPDATE,
    CLUSTER_CANCEL_MODEL_UPDATE as CANCEL_MODEL_UPDATE,
    CLUSTER_ADD_NEW as ADD_NEW,
    CLUSTER_ARTICLE_SEARCH_REQUEST,
    CLUSTER_ARTICLE_SEARCH_RESPONSE,
    CLUSTER_ARTICLE_EDIT_SELECT,
    CLUSTER_TOPIC_EDIT_SELECT
} from "../../actions/action-types"

import {
    toUI as clusterToUI,
    updateClusterChangeModel,
    updateClusterCreateModel,
    ClusterModel,
    ClusterCreateModel
} from "../../models/entities/cluster"

import {
    ClusterFilterModel,
    updateClusterSearchFilterModel
} from "../../models/filters/cluster-filter"

import { defaultSearchCriteria } from "../paging/cluster-paging-reducer"
import { serialiseSearchCriteria, idify } from "../paging/paging-reducer-utils"

const reducerBuilder = new PresentationReducerBuilder()
const defaultReducer = reducerBuilder
    .actionsToHandle({
        UPDATE_IMAGE,
        UPDATE_MODEL,
        APPLY_MODEL_UPDATE,
        CANCEL_MODEL_UPDATE,
        CLEAR_FILTER_CRITERIA,
        UPDATE_FILTER_CRITERIA,
        SET_FILTER,
        SET_EDIT_MODE,
        ITEM_REQUEST,
        ITEM_RESPONSE,
        ITEM_CACHED_RESPONSE,
        LIST_PAGE_REQUEST,
        LIST_SEARCH_REQUEST,
        LIST_RESPONSE,
        LIST_CACHED_RESPONSE,
        ADD_NEW
    })
    .updateChangeModel(updateClusterChangeModel)
    .updateCreateModel(updateClusterCreateModel)
    .workingModelToUI(clusterToUI)
    .workingModelClass(ClusterModel)
    .workingFilterModelClass(ClusterFilterModel)
    .updateSearchFilterModel(updateClusterSearchFilterModel)
    .canCancel(() => true)
    .canSave(
        (updateModel, file, workingModel) =>
            workingModel.title &&
            workingModel.title.length &&
            workingModel.description &&
            workingModel.description.length &&
            workingModel.articles &&
            workingModel.articles.length >= 2 &&
            workingModel.articles.some(({ is_headline }) => is_headline) &&
            (updateModel.size > 0 || file !== undefined)
    )
    .createModelClass(ClusterCreateModel)
    .defaultState({
        workingModel: new ClusterModel(),
        searchCriteria: serialiseSearchCriteria(defaultSearchCriteria),
        searchFilterModel: defaultSearchCriteria,
        workingFilterModel: new ClusterFilterModel(),
        isFetchingHeadlineArticleSearch: false,
        headlineArticleSearchTerm: "",
        headlineArticleSearchCriteria: "not_rejected=true&title=",
        isFetchingOtherArticlesSearch: false,
        otherArticlesSearchTerm: "",
        otherArticlesSearchCriteria: "not_rejected=true&title="
    })
    .build()

export default (state, action) => {
    const defaultReduction = defaultReducer(state, action)
    switch (action.type) {
        case ITEM_REQUEST: {
            const { isFetchingAfterSave } = action
            if (!isFetchingAfterSave) {
                return defaultReduction
            }

            return {
                ...defaultReduction,
                isFetchingItem: false,
                isSavingItem: true,
                workingModel: state.workingModel
            }
        }
        case CLUSTER_ITEM_SAVE_RESPONSE: {
            const { error } = action
            const updateModel = new Map()

            return {
                ...state,
                isFetchingItem: false,
                isSavingItem: true,
                imageToUpload: undefined,
                updateModel,
                error: error ? "Error Message" : null
            }
        }
        case CLUSTER_ARTICLE_SEARCH_REQUEST:
            const capitalisedPropertyName = `${action.name
                .charAt(0)
                .toUpperCase()}${action.name.substr(1)}`
            const isFetchingPropertyName = `isFetching${capitalisedPropertyName}Search`
            const searchTermPropertyName = `${action.name}SearchTerm`
            const searchCriteriaPropertyName = `${action.name}SearchCriteria`
            return {
                ...state,
                [isFetchingPropertyName]: !!action.value,
                [searchTermPropertyName]: action.value,
                [searchCriteriaPropertyName]: serialiseSearchCriteria({
                    not_rejected: true,
                    title: action.value
                })
            }
        case CLUSTER_ARTICLE_SEARCH_RESPONSE:
            return {
                ...state,
                isFetchingOtherArticlesSearch: false,
                isFetchingHeadlineArticleSearch: false
            }
        case CLUSTER_ARTICLE_EDIT_SELECT: {
            let { workingModel, updateModel } = state
            const { name } = action
            const articles = workingModel.get("articles")
            let { article } = action
            const isChangingHeadline = name === "headlineArticle"
            let newArticles = articles.filter(
                ({ id, is_headline }) =>
                    (!isChangingHeadline || !is_headline) && id !== article.id
            )
            if (isChangingHeadline) {
                article = {
                    ...article,
                    is_headline: true
                }
                // Set the cluster's title and image to the same as the
                // headline article
                workingModel = workingModel
                    .set("image_url", article.image)
                    .set("title", article.title)
                updateModel = updateModel.set("title", article.title)
            }
            if (newArticles.length === articles.length || isChangingHeadline) {
                // The article wasn't previously there, so add it
                newArticles = [
                    ...newArticles.map(existingArticle => ({
                        ...existingArticle,
                        is_headline:
                            existingArticle.is_headline && !isChangingHeadline
                    })),
                    article
                ]
            }
            workingModel = workingModel.set("articles", newArticles)
            updateModel = updateModel.set("article_ids", idify(newArticles))
            if (isChangingHeadline) {
                updateModel = updateModel.set("headline_article", article.id)
            }
            return {
                ...state,
                workingModel,
                updateModel,
                canSave: reducerBuilder._canSave(
                    updateModel,
                    state.imageToUpload,
                    workingModel
                ),
                canCancel: reducerBuilder._canCancel(
                    updateModel,
                    state.imageToUpload,
                    workingModel
                )
            }
        }
        case CLUSTER_TOPIC_EDIT_SELECT: {
            let { workingModel, updateModel } = state
            const { name, topic } = action
            const topics = workingModel.get(name)
            let newTopics = topics.filter(({ id }) => id !== topic.id)
            if (topics.length === newTopics.length) {
                newTopics = newTopics.concat([topic])
            }
            updateModel = reducerBuilder._updateChangeModel(
                name,
                newTopics,
                updateModel,
                workingModel
            )
            workingModel = workingModel.set(name, newTopics)
            return {
                ...state,
                workingModel,
                updateModel,
                canSave: reducerBuilder._canSave(
                    updateModel,
                    state.imageToUpload,
                    workingModel
                ),
                canCancel: reducerBuilder._canCancel(
                    updateModel,
                    state.imageToUpload,
                    workingModel
                )
            }
        }
        default:
            return defaultReduction
    }
}
